import Seo from "@/components/feature/Seo";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Container from "@/components/base/Container";
import PageHero from "@/components/base/PageHero";
import Button from "@/components/base/Button";
import VehicleCard from "@/components/feature/VehicleCard";
import { capacidadOptions, categoryLabels, categoryOptions, estadoOptions } from "@/lib/vehicleMeta";
import { useVehiculos } from "@/hooks/usePublicContent";
import type { VehicleCategory } from "@/types";

const categoriaImagenes: Record<string, string> = {
  camiones:
    "https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20modern%20white%20cargo%20trucks%20lined%20up%20on%20a%20clean%20open%20terminal%2C%20professional%20corporate%20transport%20photography%2C%20deep%20blue%20tones%2C%20high%20detail&width=1920&height=700&seq=axonia-hero-camiones&orientation=landscape",
  vans: "https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20modern%20white%20cargo%20vans%20parked%20in%20a%20clean%20urban%20logistics%20yard%2C%20professional%20corporate%20photography%2C%20deep%20blue%20tones%2C%20high%20detail&width=1920&height=700&seq=axonia-hero-vans&orientation=landscape",
  autobuses:
    "https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20modern%20white%20coach%20buses%20parked%20in%20a%20clean%20depot%20under%20a%20blue%20sky%2C%20professional%20corporate%20transport%20photography%2C%20deep%20blue%20tones%2C%20high%20detail&width=1920&height=700&seq=axonia-hero-autobuses&orientation=landscape",
  eventos:
    "https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20white%20production%20vans%20with%20stage%20equipment%20beside%20an%20outdoor%20event%20venue%20at%20dusk%2C%20professional%20event%20logistics%20photography%2C%20deep%20blue%20tones%2C%20high%20detail&width=1920&height=700&seq=axonia-hero-eventos&orientation=landscape",
  especiales:
    "https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20special%20purpose%20trucks%20with%20hydraulic%20equipment%20in%20a%20clean%20open%20industrial%20yard%2C%20professional%20industrial%20photography%2C%20deep%20blue%20tones%2C%20high%20detail&width=1920&height=700&seq=axonia-hero-especiales&orientation=landscape",
};

const defaultImagen =
  "https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20a%20mixed%20fleet%20of%20white%20trucks%20vans%20and%20buses%20in%20a%20clean%20open%20terminal%2C%20professional%20corporate%20transport%20photography%2C%20deep%20blue%20tones%2C%20high%20detail&width=1920&height=700&seq=axonia-hero-flota&orientation=landscape";

export default function Flota() {
  const { categoria } = useParams();
  const fleet = useVehiculos();

  const activeCategory = categoryOptions.some((option) => option.value === categoria)
    ? (categoria as VehicleCategory)
    : "todas";

  const [query, setQuery] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState<VehicleCategory | "todas">(activeCategory);
  const [capacidad, setCapacidad] = useState("todas");
  const [servicio, setServicio] = useState("todos");
  const [estado, setEstado] = useState("todos");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const serviciosDisponibles = useMemo(
    () => Array.from(new Set(fleet.map((vehicle) => vehicle.servicio))).sort(),
    [fleet],
  );

  const filtered = useMemo(() => {
    return fleet
      .filter((vehicle) => vehicle.activo)
      .filter((vehicle) =>
        categoriaFiltro === "todas" ? true : vehicle.categoria === categoriaFiltro,
      )
      .filter((vehicle) => (capacidad === "todas" ? true : vehicle.capacidadGrupo === capacidad))
      .filter((vehicle) => (servicio === "todos" ? true : vehicle.servicio === servicio))
      .filter((vehicle) => (estado === "todos" ? true : vehicle.estado === estado))
      .filter((vehicle) =>
        query.trim() === ""
          ? true
          : `${vehicle.nombre} ${vehicle.tipo} ${vehicle.capacidad}`
              .toLowerCase()
              .includes(query.trim().toLowerCase()),
      )
      .sort((a, b) => a.orden - b.orden);
  }, [categoriaFiltro, capacidad, servicio, estado, query, fleet]);

  const resetFilters = () => {
    setQuery("");
    setCategoriaFiltro("todas");
    setCapacidad("todas");
    setServicio("todos");
    setEstado("todos");
  };

  const title =
    categoriaFiltro === "todas"
      ? "Nuestra flota de unidades"
      : `Flota de ${categoryLabels[categoriaFiltro]}`;

  const seoPath = activeCategory === "todas" ? "/flota" : `/flota/${activeCategory}`;
  const seoTitle =
    activeCategory === "todas"
      ? "Nuestra flota de unidades"
      : `Flota de ${categoryLabels[activeCategory]}`;

  const filterSelect =
    "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm font-label text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100";

  return (
    <>
      <Seo
        title={seoTitle}
        description="Consulta disponibles camiones, furgonetas y autobuses para mudanzas, acarreo, distribución y eventos. Filtra por tipo, servicio, capacidad y disponibilidad."
        path={seoPath}
      />
      <PageHero
        title={title}
        subtitle="Consulta las unidades disponibles, su capacidad y características. Filtra por tipo, servicio, capacidad y disponibilidad."
        crumbs={[
          { label: "Inicio", to: "/" },
          { label: "Flota", to: "/flota" },
          ...(categoriaFiltro === "todas" ? [] : [{ label: categoryLabels[categoriaFiltro] }]),
        ]}
        image={categoriaImagenes[categoriaFiltro] ?? defaultImagen}
      />

      <section data-product-shop className="bg-background-50 py-12 md:py-16">
        <Container>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCategoriaFiltro("todas")}
              className={`rounded-full px-4 py-2 text-sm font-label font-medium transition-colors duration-200 cursor-pointer ${
                categoriaFiltro === "todas"
                  ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                  : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
              }`}
            >
              Todas las unidades
            </button>
            {categoryOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setCategoriaFiltro(option.value)}
                className={`rounded-full px-4 py-2 text-sm font-label font-medium transition-colors duration-200 cursor-pointer ${
                  categoriaFiltro === option.value
                    ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                    : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-base leading-none" aria-hidden="true"></i>
                </span>
                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Buscar por nombre, tipo o capacidad..."
                  aria-label="Buscar unidades"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <button
                type="button"
                onClick={() => setFiltersOpen((prev) => !prev)}
                aria-expanded={filtersOpen}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm font-label font-medium text-foreground-700 transition-colors duration-200 hover:bg-background-100 cursor-pointer lg:hidden"
              >
                <i className="ri-equalizer-line text-base leading-none" aria-hidden="true"></i>
                Filtros
              </button>
            </div>

            <div className={`${filtersOpen ? "grid" : "hidden"} mt-4 grid-cols-1 gap-3 sm:grid-cols-2 lg:mt-4 lg:grid lg:grid-cols-3`}>
              <div>
                <label htmlFor="filtro-capacidad" className="mb-1.5 block text-xs font-label font-medium text-foreground-500">
                  Capacidad
                </label>
                <select
                  id="filtro-capacidad"
                  value={capacidad}
                  onChange={(event) => setCapacidad(event.target.value)}
                  className={filterSelect}
                >
                  <option value="todas">Cualquier capacidad</option>
                  {capacidadOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="filtro-servicio" className="mb-1.5 block text-xs font-label font-medium text-foreground-500">
                  Servicio
                </label>
                <select
                  id="filtro-servicio"
                  value={servicio}
                  onChange={(event) => setServicio(event.target.value)}
                  className={filterSelect}
                >
                  <option value="todos">Todos los servicios</option>
                  {serviciosDisponibles.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="filtro-estado" className="mb-1.5 block text-xs font-label font-medium text-foreground-500">
                  Disponibilidad
                </label>
                <select
                  id="filtro-estado"
                  value={estado}
                  onChange={(event) => setEstado(event.target.value)}
                  className={filterSelect}
                >
                  <option value="todos">Toda la disponibilidad</option>
                  {estadoOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4">
              <p className="text-sm text-foreground-600">
                <strong className="font-semibold text-foreground-950">{filtered.length}</strong>{" "}
                {filtered.length === 1 ? "unidad encontrada" : "unidades encontradas"}
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 text-sm font-label font-medium text-primary-600 transition-colors hover:text-primary-700 cursor-pointer"
              >
                <i className="ri-refresh-line text-base leading-none" aria-hidden="true"></i>
                Limpiar filtros
              </button>
            </div>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((vehicle, index) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} index={index} />
              ))}
            </div>
          ) : (
            <div className="mt-12 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-inbox-line text-2xl leading-none" aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 text-base font-heading font-semibold text-foreground-950">
                No encontramos unidades con esos filtros
              </h3>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                Prueba ajustando la búsqueda o limpiando los filtros. También puedes solicitar una
                unidad específica y te ayudamos a conseguirla.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Button variant="outline" size="md" icon="ri-refresh-line" onClick={resetFilters}>
                  Limpiar filtros
                </Button>
                <Button to="/solicitar-servicio" variant="primary" size="md" icon="ri-send-plane-line">
                  Solicitar servicio
                </Button>
              </div>
            </div>
          )}
        </Container>
      </section>
    </>
  );
}